import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Alert,
    Switch,
    FormControlLabel,
    Card,
    CardContent,
    Divider,
    CircularProgress,
    Chip
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import SyncIcon from '@mui/icons-material/Sync';
import SyncDisabledIcon from '@mui/icons-material/SyncDisabled';
import GoogleIcon from '@mui/icons-material/Google';

const CalendarSettings = () => {
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState({
        isConnected: false,
        googleCalendarSync: false
    });
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        fetchCalendarStatus();

        // Check URL parameters for callback messages
        const urlParams = new URLSearchParams(window.location.search);
        const calendarParam = urlParams.get('calendar');

        if (calendarParam === 'connected') {
            setMessage('Google Calendar connected successfully!');
            fetchCalendarStatus(); // Refresh status
            // Clean URL
            window.history.replaceState({}, document.title, window.location.pathname);
        } else if (calendarParam === 'error') {
            setError('Failed to connect Google Calendar. Please try again.');
            // Clean URL
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, []);

    const fetchCalendarStatus = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/calendar/status', {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setStatus(data);
            }
        } catch (error) {
            console.error('Error fetching calendar status:', error);
        }
    };

    const handleConnectGoogle = async () => {
        setLoading(true);
        setError('');
        setMessage('');

        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/calendar/auth-url', {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                // Redirect to Google OAuth
                window.location.href = data.authUrl;
            } else {
                throw new Error('Failed to get authorization URL');
            }
        } catch (error) {
            console.error('Error connecting to Google Calendar:', error);
            setError('Failed to connect to Google Calendar. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleDisconnect = async () => {
        setLoading(true);
        setError('');
        setMessage('');

        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/calendar/disconnect', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                setMessage('Google Calendar disconnected successfully!');
                setStatus({
                    isConnected: false,
                    googleCalendarSync: false
                });
            } else {
                throw new Error('Failed to disconnect');
            }
        } catch (error) {
            console.error('Error disconnecting Google Calendar:', error);
            setError('Failed to disconnect Google Calendar. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ maxWidth: 800, margin: '0 auto', px: { xs: 0, sm: 2 } }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                Calendar Integration
            </Typography>

            {message && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>
                    {message}
                </Alert>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Card>
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <GoogleIcon sx={{ mr: 2, fontSize: '2rem', color: '#4285f4' }} />
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                                    Google Calendar
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Sync your class registrations with Google Calendar
                                </Typography>
                            </Box>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {status.isConnected ? (
                                    <>
                                        <SyncIcon sx={{ mr: 1, color: 'success.main' }} />
                                        <Chip
                                            label="Connected"
                                            color="success"
                                            size="small"
                                            sx={{ mr: 2 }}
                                        />
                                    </>
                                ) : (
                                    <>
                                        <SyncDisabledIcon sx={{ mr: 1, color: 'grey.500' }} />
                                        <Chip
                                            label="Not Connected"
                                            color="default"
                                            size="small"
                                            sx={{ mr: 2 }}
                                        />
                                    </>
                                )}

                                <Typography variant="body2" color="text.secondary">
                                    {status.isConnected
                                        ? 'Your classes will be automatically synced to your Google Calendar'
                                        : 'Connect to automatically sync class registrations'
                                    }
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            {!status.isConnected ? (
                                <Button
                                    variant="contained"
                                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <GoogleIcon />}
                                    onClick={handleConnectGoogle}
                                    disabled={loading}
                                    sx={{
                                        backgroundColor: '#4285f4',
                                        '&:hover': {
                                            backgroundColor: '#3367d6',
                                        },
                                        borderRadius: 20,
                                        px: 3
                                    }}
                                >
                                    {loading ? 'Connecting...' : 'Connect Google Calendar'}
                                </Button>
                            ) : (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SyncDisabledIcon />}
                                    onClick={handleDisconnect}
                                    disabled={loading}
                                    sx={{
                                        borderRadius: 20,
                                        px: 3
                                    }}
                                >
                                    {loading ? 'Disconnecting...' : 'Disconnect'}
                                </Button>
                            )}
                        </Box>

                        {status.isConnected && (
                            <Box sx={{
                                mt: 3,
                                p: 2,
                                backgroundColor: 'success.50',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'success.200'
                            }}>
                                <Typography variant="body2" color="success.main" sx={{
                                    fontWeight: 'medium',
                                    mb: 1,
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>
                                    <EventIcon sx={{ mr: 1, fontSize: '1rem' }} />
                                    Calendar Sync Active
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    • New class registrations will be automatically added to your calendar
                                    <br />
                                    • Cancelled registrations will be removed from your calendar
                                    <br />
                                    • Events include class details, trainer info, and location

                                </Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Paper>

            <Paper elevation={1} sx={{
                mt: 3,
                p: 3,
                backgroundColor: 'info.50',
                border: '1px solid',
                borderColor: 'info.200'
            }}>
                <Typography variant="body2" color="info.main" sx={{ fontWeight: 'medium', mb: 1 }}>
                    How Calendar Sync Works:
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    When you register for a class, it will automatically appear in your Google Calendar with all the relevant details.
                    If you cancel a registration, the event will be removed from your calendar.
                    This helps you keep track of your workout schedule without manual entry.
                </Typography>

                <Typography variant="body2" color="text.secondary">
                    <strong>Privacy:</strong> Only you can see and control this integration. Your calendar data remains private,
                    and you can disconnect at any time. We only add/remove events related to your class registrations.
                </Typography>
            </Paper>
        </Box>
    );
};

export default CalendarSettings;